"use client";

import { useState, useEffect } from "react";
import { Bell, Menu, X, FileText, LogOut } from "lucide-react";
import "./header.css";
import MaleLogo from "../../Assets/Images/man.png";
import FemaleLogo from "../../Assets/Images/woman.png";
import axiosInstance from "../../utils/axiosInstance";
import { BASE_URL } from "../../config";

export default function Header({
  searchQuery,
  setSearchQuery,
  toggleSidebar,
  showMenuButton,
}) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [activeSection, setActiveSection] = useState("");
  const [activeColorHex, setActiveColorHex] = useState("#3b82f6");

  // State for dynamic user data (updates on localStorage change)
  const [roleName, setRoleName] = useState("Guest");
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("User");
  const [email, setEmail] = useState("No email");
  const [gender, setGender] = useState("MALE");
  const [token, setToken] = useState(null);
  const [userType, setUserType] = useState(null);

  // Safely read user data from localStorage
  const getUserData = () => {
    try {
      return JSON.parse(localStorage.getItem("NagpurProperties")) || {};
    } catch {
      return {};
    }
  };

  // Extract and set all user-related data + active section
  const syncFromLocalStorage = () => {
    const userData = getUserData();

    // Update active section/color if changed
    const newActiveSection = userData.activeSection || "";
    const newActiveColorHex = userData.activeColorHex || "#3b82f6";
    if (
      newActiveSection !== activeSection ||
      newActiveColorHex !== activeColorHex
    ) {
      setActiveSection(newActiveSection);
      setActiveColorHex(newActiveColorHex);
      console.log("Updated header active section/color:", {
        newActiveSection,
        newActiveColorHex,
      });
    }

    // Update role and user info if changed
    const newRoleName = userData.role?.[0]?.roleName || "Guest";
    if (newRoleName !== roleName) setRoleName(newRoleName);

    let newUserId = null;
    let newUserName = "User";
    let newEmail = "No email";
    let newGender = userData?.gender || "MALE";
    const newToken = userData?.token || userData?.jwtToken;
    const newUserType = newRoleName?.toUpperCase();

    if (newRoleName === "Admin" || newRoleName === "SuperAdmin") {
      newUserId = userData.id;
      newUserName = userData.name;
      newEmail = userData.email;
    } else if (newRoleName === "Head") {
      newUserId = userData.headId;
      newUserName = userData.name;
      newEmail = userData.emailId;
    } else if (newRoleName === "Employee") {
      newUserId = userData.employeeId;
      newUserName = userData.employeeName;
      newEmail = userData.emailId;
    }  else if (newRoleName === "Engineer" || newRoleName === "SuperEngineer") {
      newUserId = userData.id;
      newUserName = userData.name;
      newEmail = userData.email;
    } 
    
    else if (newRoleName === "Partner") {
      const partner = userData.lands?.[0]?.partners?.[0];
      if (partner) {
        newUserId = partner.id;
        newUserName = partner.name;
        newEmail = partner.email;
        newGender = partner.gender || "MALE";
      }
    }

    setUserId(newUserId);
    setUserName(newUserName);
    setEmail(newEmail);
    setGender(newGender);
    setToken(newToken);
    setUserType(newUserType);

    console.log("Synced user data from localStorage"); // Debug: Confirm trigger
  };

  // Run on mount and on localStorage updates
  useEffect(() => {
    syncFromLocalStorage(); // Initial sync

    const handleStorageChange = (e) => {
      if (e.key === "NagpurProperties") {
        syncFromLocalStorage();
      }
    };

    const handleCustomStorageChange = () => {
      syncFromLocalStorage();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "localStorageUpdated",
        handleCustomStorageChange
      );
    };
  }, []); // Empty deps: Listeners are stable, sync reads current state

  const fetchNotificationCount = async () => {
    if (!userId || !userType || !token) return;

    try {
      const url =
        roleName === "Partner"
          ? `${BASE_URL}/partner/${userId}/count/unread-notification`
          : `${BASE_URL}/get/count/unread/notification?userId=${userId}&userType=${userType}`;

      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const count =
        typeof response.data === "number"
          ? response.data
          : response.data?.count ?? 0;
      setNotificationCount(count);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("NagpurProperties");
        window.location.href = "/login";
      }
      setNotificationCount(0);
    }
  };

  const fetchNotifications = async () => {
    if (!userId || !userType || !token) return;

    try {
      const url =
        roleName === "Partner"
          ? `${BASE_URL}/partner/${userId}/get/all/notification`
          : `${BASE_URL}/all-notification?userId=${userId}&userType=${userType}`;

      const response = await axiosInstance.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const uniqueNotifications = Array.from(
        new Map(
          response.data.map((item) => [item.notificationId, item])
        ).values()
      );

      setNotifications(uniqueNotifications);
      fetchNotificationCount();
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("NagpurProperties");
        window.location.href = "/login";
      }
      setNotifications([]);
    }
  };

  const isPastDue = (scheduledTime) => {
    const now = new Date();
    const [datePart, timePart] = (scheduledTime || "").split("at ");
    if (!datePart || !timePart) return false;

    const [day, month, year] = datePart.split(" ");
    const [hours, minutes] = timePart.split(":");
    const monthMap = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };
    const scheduled = new Date(
      `${year}-${monthMap[month]}-${day.padStart(
        2,
        "0"
      )}T${hours}:${minutes}:00+05:30`
    );
    return scheduled < now;
  };

  const handleLogOut = () => {
    const confirmation = window.confirm("Are you sure you want to log out?");
    if (!confirmation) return;
    localStorage.removeItem("NagpurProperties");
    window.dispatchEvent(new Event("localStorageUpdated")); // Trigger update if needed
    window.location.href = "/login";
  };

  useEffect(() => {
    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 60000);
    return () => clearInterval(interval);
  }, [userId, userType, token, roleName]); // Added roleName to deps for safety

  return (
    <header className="dashboard-header">
      {showMenuButton && (
        <button
          className="menu-button"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <Menu size={22} />
        </button>
      )}

      {activeSection && (
        <div className="active-section-display" aria-live="polite">
          <h2
            className="active-section-value"
            style={{ color: activeColorHex }}
          >
            {activeSection}
          </h2>
        </div>
      )}

      <div className="header-actions">
        {["Admin","SuperAdmin", "Head", "Employee", "Partner","Engineer", "SuperEngineer"].includes(roleName) && (
          <>
            <button className="header-button" type="button">
              <span>{roleName} Panel</span>
            </button>

            <div className="notification-wrapper">
              <button
                className="icon-button notification-button"
                onClick={() => {
                  const next = !showNotifications;
                  setShowNotifications(next);
                  if (next) fetchNotifications();
                  else setNotifications([]);
                  if (showUserMenu) setShowUserMenu(false);
                }}
                aria-label="Notifications"
                aria-expanded={showNotifications}
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <span
                    className="notification-badge"
                    aria-label={`${notificationCount} unread`}
                  >
                    {notificationCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="notifications-dropdown">
                  <div className="notifications-header">
                    <h3>Notifications</h3>
                    <button
                      className="close-button"
                      onClick={() => setShowNotifications(false)}
                      aria-label="Close notifications"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="notifications-list">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => {
                        const past = isPastDue(
                          notif.body?.match(/scheduled for (.*)/)?.[1] || ""
                        );
                        return (
                          <div
                            key={notif.notificationId}
                            className={`notification-item ${
                              past ? "past-due" : "unread"
                            }`}
                          >
                            <div
                              className={`notification-icon ${
                                past ? "red" : "blue"
                              }`}
                            >
                              <FileText size={16} />
                            </div>
                            <div className="notification-content">
                              <p className="notification-text">{notif.body}</p>
                              <p className="notification-time">
                                {notif.sentAgo} ago
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="notification-item">
                        No notifications available
                      </div>
                    )}
                  </div>
                  <div className="notifications-footer">
                    <button className="view-all" type="button">
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="user-profile-wrapper">
          <div
            className="user-profile"
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              if (showNotifications) setShowNotifications(false);
            }}
            role="button"
            tabIndex={0}
          >
            <div className="user-avatar">
              <img
                src={gender?.toUpperCase() === "MALE" ? MaleLogo : FemaleLogo}
                alt="User avatar"
                className="user-avatar-image"
              />
            </div>
            <div className="user-info">
              <span className="user-name">{userName}</span>
              <span className="user-role">{roleName}</span>
            </div>
          </div>

          {showUserMenu && (
            <div className="user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">
                  <img
                    src={
                      gender?.toUpperCase() === "MALE" ? MaleLogo : FemaleLogo
                    }
                    alt="User avatar"
                    className="user-avatar-image"
                  />
                </div>
                <div>
                  <h4 className="user-dropdown-name">{userName}</h4>
                  <p className="user-dropdown-email">{email}</p>
                </div>
              </div>
              <div className="user-dropdown-footer">
                <a
                  href="#"
                  className="user-dropdown-item logout"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogOut();
                  }}
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
